import type { AdditionLevelParams } from '../progression/levels.js';
import { pickRandom, shuffle } from './random.js';

/**
 * Addition distractors are plausible-mistake values: the operands themselves,
 * sum±1, and |A−B| (a "subtracted instead" mistake), clipped to the digit
 * range and excluding the true sum.
 */
export function pickAdditionDistractors(
  a: number,
  b: number,
  sumCap: number,
  distractorCount: number,
  rng: () => number,
): number[] {
  if (distractorCount <= 0) return [];
  const sum = a + b;
  const candidates = new Set<number>();
  for (const v of [a, b, sum + 1, sum - 1, Math.abs(a - b)]) {
    if (v >= 0 && v <= sumCap && v !== sum) candidates.add(v);
  }
  // If we still don't have enough, pad with neighbors of the sum.
  for (const delta of [2, -2, 3, -3]) {
    if (candidates.size >= distractorCount) break;
    const v = sum + delta;
    if (v >= 0 && v <= sumCap && v !== sum) candidates.add(v);
  }
  const pool = Array.from(candidates);
  const distractors: number[] = [];
  while (distractors.length < distractorCount && pool.length > 0) {
    const idx = Math.floor(rng() * pool.length);
    const [picked] = pool.splice(idx, 1);
    distractors.push(picked!);
  }
  return distractors;
}

export interface AdditionTrial {
  a: number;
  b: number;
  /** True sum (a + b). */
  sum: number;
  /** Digit-tile choices in display order. */
  tiles: number[];
}

export function generateAdditionTrial(
  params: AdditionLevelParams,
  rng: () => number = Math.random,
): AdditionTrial {
  const { sumCap, allowOperandZero } = params;
  const minOperand = allowOperandZero ? 0 : 1;

  // Build all valid (a, b) pairs with a + b ≤ sumCap (and at least one operand
  // nonzero so we don't end up with 0 + 0 = 0).
  const pairs: Array<[number, number]> = [];
  for (let a = minOperand; a <= sumCap; a++) {
    for (let b = minOperand; b <= sumCap; b++) {
      if (a + b > sumCap) break;
      if (a === 0 && b === 0) continue;
      pairs.push([a, b]);
    }
  }
  const [a, b] = pickRandom(pairs, rng);
  const sum = a + b;
  const distractors = pickAdditionDistractors(a, b, sumCap, params.tileCount - 1, rng);
  const tiles = shuffle([sum, ...distractors], rng);
  return { a, b, sum, tiles };
}
